import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocalizedSteps } from '../stationForm';

interface ActionsSidebarProps {
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  error?: string | null;
  isSuccess?: boolean;
  mode?: 'create' | 'edit';
}

export const ActionsSidebar = memo(function ActionsSidebar({
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onSubmit,
  onCancel,
  isSubmitting,
  isFirstStep,
  isLastStep,
  error,
  isSuccess = false,
  mode = 'create',
}: ActionsSidebarProps) {
  const { t } = useTranslation();
  const isEditMode = mode === 'edit';

  // On mobile the sidebar is a fixed bottom sheet that overlaps the form.
  // The handle lets the user collapse it out of the way (no-op visually on
  // desktop, where the handle is hidden and the sidebar scrolls normally).
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Step titles come from the same STEP_DEFS the stepper renders.
  const localizedSteps = useLocalizedSteps();

  const submitLabels = useMemo(
    () => ({
      create: {
        idle: t('dashboard.createStationV2.createStation'),
        busy: t('dashboard.createStationV2.creating'),
      },
      edit: {
        idle: t('dashboard.createStationV2.saveChanges'),
        busy: t('dashboard.createStationV2.saving'),
      },
    }),
    [t],
  );
  const submitLabel = submitLabels[mode][isSubmitting ? 'busy' : 'idle'];

  const helpItemsByStep = useMemo(
    () => [
      [
        { icon: 'info', text: t('dashboard.createStationV2.helpGeneral1') },
        { icon: 'bolt', text: t('dashboard.createStationV2.helpGeneral2') },
      ],
      [
        { icon: 'help_outline', text: t('dashboard.createStationV2.helpLocation1') },
        { icon: 'location_on', text: t('dashboard.createStationV2.helpLocation2') },
      ],
      [
        { icon: 'schedule', text: t('dashboard.createStationV2.helpSchedule1') },
        { icon: 'verified', text: t('dashboard.createStationV2.helpSchedule2') },
      ],
    ],
    [t],
  );

  const helpItems = helpItemsByStep[currentStep] || helpItemsByStep[0];

  return (
    <aside className="actions-sidebar">
      <div className={`actions-card${isCollapsed ? ' actions-card--collapsed' : ''}`}>
        <button
          type="button"
          className="actions-handle"
          onClick={() => setIsCollapsed((prev) => !prev)}
          aria-expanded={!isCollapsed}
          aria-label={
            isCollapsed
              ? t('dashboard.createStationV2.showActions')
              : t('dashboard.createStationV2.hideActions')
          }
        >
          <span className="actions-handle-grip" aria-hidden="true" />
          <span className="material-symbols-outlined actions-handle-icon">
            {isCollapsed ? 'expand_less' : 'expand_more'}
          </span>
        </button>

        {isSuccess && (
          <div className="success-message">
            <span className="material-symbols-outlined success-icon">check_circle</span>
            <div>
              <p className="success-title">
                {isEditMode
                  ? t('dashboard.createStationV2.successUpdated')
                  : t('dashboard.createStationV2.successCreated')}
              </p>
              <p className="success-text">
                {isEditMode
                  ? t('dashboard.createStationV2.successUpdatedHint')
                  : t('dashboard.createStationV2.successCreatedHint')}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <span className="material-symbols-outlined error-icon">error</span>
            <p className="error-text">{error}</p>
          </div>
        )}

        <div className="step-indicator">
          <span className="step-number">
            {t('dashboard.createStationV2.stepIndicator', {
              current: currentStep + 1,
              total: totalSteps,
            })}
          </span>
          <span className="step-name">{localizedSteps[currentStep]?.label}</span>
        </div>

        <div className="actions-buttons">
          {isLastStep ? (
            <button
              type="button"
              className="btn-primary"
              onClick={onSubmit}
              disabled={isSubmitting}
            >
              {submitLabel}
              <span className="material-symbols-outlined btn-icon">{isEditMode ? 'save' : 'bolt'}</span>
            </button>
          ) : (
            <button type="button" className="btn-primary" onClick={onNext}>
              {t('dashboard.createStationV2.nextStep')}
              <span className="material-symbols-outlined btn-icon">arrow_forward</span>
            </button>
          )}

          {!isFirstStep && (
            <button
              type="button"
              className="btn-outline"
              onClick={onPrevious}
              disabled={isSubmitting}
            >
              <span className="material-symbols-outlined btn-icon-left">arrow_back</span>
              {t('dashboard.createStationV2.previous')}
            </button>
          )}

          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('dashboard.createStationV2.cancel')}
          </button>
        </div>

        <div className="help-center">
          <h4 className="help-title">{t('dashboard.createStationV2.helpCenter')}</h4>
          <div className="help-items">
            {helpItems.map((item, index) => (
              <div key={index} className="help-item">
                <span className="material-symbols-outlined help-icon">
                  {item.icon}
                </span>
                <p className="help-text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
});

export default ActionsSidebar;
